import { Chip, LinearProgress, Typography } from '@mui/material'
import React, { useCallback, useEffect, useMemo } from 'react'
import { KcTable } from '../common/kc-table'
import { CustomNoRowsOverlay } from '../common/kc-table/components'
import dayjs from 'dayjs'
import { useDispatch, useSelector } from 'react-redux'
import { useAxiosPrivate } from '../../hooks'
import {
  fetchDicomLogs,
  getDicomLogs,
  getDicomLogsError,
  getDicomLogsLoading,
} from '../../pages/slices/dicomLog.slice'
import {
  getFetchError,
  getFetchLoading,
} from '../../pages/slices/facility.slice'
import { API_URL } from '../../api/constants'
import {
  formatDateTime,
  formatTimeRemaining,
} from '../../pages/HL7historyLogs/utils'
import Layout from '../../layouts/common/Layout'
import AccessRestrictedMessage from './AccessRestricted'

const columns = [
  {
    field: 'orderNumber',
    headerName: 'Order Number',
    minWidth: 100,
    flex: 1,
    renderCell: (params) => {
      return (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
          }}
        >
          {params?.value || '-'}
        </Typography>
      )
    },
  },
  {
    field: 'status',
    headerName: 'Status',
    minWidth: 100,
    flex: 1,
    renderCell: (params) => {
      return (
        <Chip
          size="small"
          label={params?.value === 0 ? 'Failed' : 'Success'}
          color={params?.value === 0 ? 'error' : 'success'}
        />
      )
    },
  },
  {
    field: 'retryCount',
    headerName: 'Retry Count',
    minWidth: 100,
    flex: 1,
    renderCell: (params) => {
      return (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
          }}
        >
          {params?.value || 0}
        </Typography>
      )
    },
  },
  {
    field: 'recordedAt',
    headerName: 'Recorded At',
    minWidth: 200,
    flex: 1,
    type: 'dateTime',
    renderCell: (params) =>
      params?.value ? formatDateTime(params.value) : 'N/A',
    sortComparator: (v1, v2) => {
      return dayjs(v1).isAfter(dayjs(v2)) ? 1 : -1
    },
  },
  {
    field: 'sentAt',
    headerName: 'Sent At',
    minWidth: 200,
    flex: 1,
    type: 'dateTime',
    renderCell: (params) =>
      params?.value ? formatDateTime(params.value) : 'N/A',
    sortComparator: (v1, v2) => {
      return dayjs(v1).isAfter(dayjs(v2)) ? 1 : -1
    },
  },
  {
    field: 'processingTime',
    headerName: 'Processing Time',
    minWidth: 100,
    flex: 1,
    renderCell: ({ row }) => {
      return (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
          }}
        >
          {formatTimeRemaining(row.recordedAt, row.sentAt) || 'N/A'}
        </Typography>
      )
    },
  },
]

function DicomAuditLogTable({ mrn, hasHl7LogAccess = false }) {
  const axiosPrivate = useAxiosPrivate()
  const dispatch = useDispatch()
  const [pageNo, setPage] = React.useState(0)
  const [pageSize, setPageSize] = React.useState(10)

  const dicomeSenderLogsData = useSelector(getDicomLogs)
  const error = useSelector(getDicomLogsError)
  const loading = useSelector(getDicomLogsLoading)
  const fetchLoading = useSelector(getFetchLoading)
  const fetchError = useSelector(getFetchError)

  const queryOptions = useMemo(() => ({ pageNo, pageSize }), [pageNo, pageSize])

  const buildQueryString = useCallback(
    (options) => {
      const queryParams = {
        pageNo,
        pageSize,
        mrn,
        ...(options?.filter && options?.value
          ? { [options.filter]: options.value }
          : {}),
        ...(options?.startDate && options?.endDate
          ? {
              from_date: dayjs(options.startDate).format('YYYY-MM-DD'),
              till_date: dayjs(options.endDate).format('YYYY-MM-DD'),
            }
          : {}),
      }

      return Object.entries(queryParams)
        .map(
          ([key, value]) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
        )
        .join('&')
    },
    [pageNo, pageSize, mrn]
  )

  const fetchDicomLogsData = useCallback(
    (options = {}) => {
      const queryString = buildQueryString(options)
      dispatch(
        fetchDicomLogs({
          axiosInstance: axiosPrivate,
          path: `${API_URL.DICOM_SENDER_LOGS}?${queryString}`,
        })
      )
    },
    [axiosPrivate, buildQueryString, dispatch]
  )

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search)
    fetchDicomLogsData({
      ...queryOptions,
      filter: queryParams.get('filter'),
      value: queryParams.get('value'),
      startDate: queryParams.get('startDate'),
      endDate: queryParams.get('endDate'),
      mrn: queryParams.get('mrn'),
    })
  }, [queryOptions, fetchDicomLogsData])
  const initialState = {
    sorting: {
      sortModel: [{ field: 'recordedAt', sort: 'desc' }],
    },
  }

  const totalCount = dicomeSenderLogsData?.totalCount || 0

  const renderError = () => {
    if (fetchError) {
      return (
        <Layout.CenterWrapper>
          <Typography variant="h6" color="error">
            Error fetching facilities. Please try again later.
          </Typography>
        </Layout.CenterWrapper>
      )
    }
    console.log('Error fetching HL7 logs:', error)
    if (error?.status === 401) {
      return (
        <Layout.CenterWrapper>
          <Typography variant="h6" color="error">
            Unauthorized access. Please log in again.
          </Typography>
        </Layout.CenterWrapper>
      )
    }
    return (
      <Layout.CenterWrapper>
        <Typography variant="h6" color="error">
          An error occurred while fetching HL7 logs. Please try again later.
        </Typography>
      </Layout.CenterWrapper>
    )
  }

  if (error && error?.status !== 421) {
    return renderError()
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 200,
      }}
    >
      <KcTable
        rows={dicomeSenderLogsData?.listOfLogs || []}
        autoHeight
        maxHeight="100%"
        columns={columns}
        getRowId={(row) => row.id}
        rowCount={totalCount || 0}
        loading={loading || fetchLoading}
        sortingOrder={['desc', 'asc']}
        initialState={initialState}
        pageNo={pageNo}
        pageSize={pageSize}
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        pagination
        paginationMode="server"
        removeToolbar
        removePadding
        components={{
          LoadingOverlay: LinearProgress,
          NoResultsOverlay: () => (
            <CustomNoRowsOverlay text="No Dicom Audit Logs Available" />
          ),
          NoRowsOverlay: () => (
            <CustomNoRowsOverlay
              text={
                !hasHl7LogAccess ? (
                  <AccessRestrictedMessage />
                ) : (
                  'No Dicom Audit Logs Found'
                )
              }
            />
          ),
        }}
      />
    </div>
  )
}

export default DicomAuditLogTable
